import * as React from "react";
import { MediaQuery } from "react-responsive";
import { Transition, TransitionGroup } from "react-transition-group";
import { Anchor } from "../../typography/anchor/anchor.mjs";
import { Container } from "../../container/container.mjs";
import { Grid } from "../../grid/grid/grid.mjs";
import { GridColumn } from "../../grid/grid-column/grid-column.mjs";
import { HeaderContext } from "../../helpers/header-provider/header-provider.mjs";
import { isScrollDisabled, doScrolling, getOffsetFromParent } from "../../helpers/helper/index.mjs";
import { Logo } from "../../logo/telia/logo.mjs";
import { PropsChecker } from "../../helpers/props-checker/props-checker.mjs";
import { SkipLinks } from "../../skip-links/skip-links.mjs";
import { breakPointOrder } from "../../helpers/theme-provider/theme-provider.mjs";
import { useTheme } from "../../helpers/theme-provider/use-theme.mjs";
import { HeaderActions } from "../header-actions/header-actions.mjs";
import { HeaderEmployee } from "../header-employee/header-employee.mjs";
import { HeaderLanguages } from "../header-languages/header-languages.mjs";
import { HeaderNav } from "../header-nav/header-nav.mjs";
import { HeaderOverlayNotice } from "../header-overlay-notice/header-overlay-notice.mjs";
import { HeaderSearch } from "../header-search/header-search.mjs";
import { HeaderSegment } from "../header-segment/header-segment.mjs";
import { HeaderInnerContext } from "./header-inner-context.mjs";
import { useCurrentBreakPoint } from "../../helpers/helper/hooks/use-current-breakpoint.mjs";
const Header = (props) => {
  const { media, portalRoot } = useTheme();
  const currentBreak = useCurrentBreakPoint();
  return /* @__PURE__ */ React.createElement(
    PropsChecker,
    {
      componentProps: props,
      componentName: "Header",
      defaultValueName: "defaultOpenSection",
      valueName: "openSection",
      onChangeNames: "onOpenSectionChange"
    },
    /* @__PURE__ */ React.createElement(
      HeaderInner,
      {
        ...props,
        media,
        currentBreak,
        portalContainer: props.portalContainer !== void 0 ? props.portalContainer : portalRoot
      }
    )
  );
};
const _HeaderInner = class _HeaderInner extends React.Component {
  constructor(props) {
    var _a;
    super(props);
    this.bottomMainElement = React.createRef();
    this.element = React.createRef();
    this.prevScrollTop = 0;
    this.navDropdownContent = null;
    this.containerRef = React.createRef();
    this.overlayRef = React.createRef();
    this.setOpenSection = (openSection) => {
      this.onOpenSectionChange(openSection, this.openSection);
    };
    this.handleScroll = () => {
      if (!isScrollDisabled()) {
        const documentHeight = document.documentElement ? document.documentElement.offsetHeight : 0;
        const windowHeight = window.innerHeight;
        const scrollTop = Math.ceil(window.scrollY || (document.documentElement ? document.documentElement.scrollTop : 0));
        const headerHeight = this.element.current ? this.element.current.clientHeight : 0;
        if (scrollTop >= headerHeight && !this.state.hasScrolled) {
          if (!this.state.hasScrolled) {
            this.setState({
              hasScrolled: true
            });
          }
        } else if (scrollTop < headerHeight && this.state.hasScrolled) {
          if (this.state.hasScrolled) {
            this.setState({
              hasScrolled: false
            });
          }
        }
        const isScrollingTop = this.prevScrollTop - scrollTop >= 2;
        if (isScrollingTop && !this.state.isScrollingTop || windowHeight + scrollTop >= documentHeight - headerHeight) {
          this.setState({
            isScrollingTop: true
          });
        } else if (this.prevScrollTop < scrollTop && this.state.isScrollingTop) {
          this.setState({
            isScrollingTop: false
          });
        }
        this.prevScrollTop = scrollTop;
      }
    };
    this.handleResize = () => {
      this.setHeaderHeight();
      this.handleMinimizedSearch();
    };
    this.handleMinimizedSearch = () => {
      this.setState({
        isSearchMinimized: this.isSearchOverlappingNav()
      });
    };
    this.isSearchOverlappingNav = () => {
      var _a2;
      const headerNavElement = document.getElementById("header-nav");
      let headerSearchElement;
      if ((_a2 = this.props.search) == null ? void 0 : _a2.id) {
        headerSearchElement = document.getElementById(this.props.search.id);
      } else {
        headerSearchElement = document.getElementById("header-search");
      }
      const navRect = headerNavElement && headerNavElement.getBoundingClientRect();
      const searchRect = headerSearchElement && headerSearchElement.getBoundingClientRect();
      if (navRect && searchRect) {
        return !(navRect.top > searchRect.bottom || navRect.right < searchRect.left || navRect.bottom < searchRect.top || navRect.left > searchRect.right);
      }
      return false;
    };
    this.onActionClick = (section) => {
      const prevOpenSection = this.openSection;
      const nextOpenSection = prevOpenSection === section ? null : section;
      this.onOpenSectionChange(nextOpenSection, prevOpenSection);
    };
    this.handleOverlayExited = () => {
      if (this.state.isOverlayVisible) {
        this.setState({
          isOverlayVisible: false
        });
      }
    };
    this.handleOverlayEntered = () => {
      if (!this.state.isOverlayVisible) {
        this.setState({
          isOverlayVisible: true
        });
      }
    };
    this.handleItemClick = (element) => {
      if (this.navDropdownContent && element) {
        doScrolling(getOffsetFromParent(element, this.navDropdownContent).top, 500, this.navDropdownContent);
      }
    };
    this.handleItemToggle = (isAnySubNavOpen) => {
      const prevOpenSection = this.openSection;
      if (!isAnySubNavOpen) {
        if (window.matchMedia("(min-width: ".concat(this.props.media.width.lg.min, ")")).matches && prevOpenSection === "nav") {
          this.onOpenSectionChange(null, prevOpenSection);
        }
      } else {
        this.onOpenSectionChange("nav", prevOpenSection);
      }
    };
    this.onMediaQueryChange = (matches) => {
      this.setState({
        isLargeScreen: matches
      });
    };
    this.handleBottomMainEntered = () => {
      if (this.state.isBottomMainVisible) {
        this.setState({
          isBottomMainVisible: false
        });
      }
    };
    this.handleBottomMainExited = () => {
      if (!this.state.isBottomMainVisible) {
        this.setState({
          isBottomMainVisible: true
        });
      }
    };
    this.shouldShowSection = (section) => {
      return this.openSection === section && this.state.isBottomMainVisible && this.state.isMounted;
    };
    this.handleFocus = (event) => {
      var _a2;
      if (((_a2 = this.element.current) == null ? void 0 : _a2.contains(event.target)) && event.target.matches(":focus-visible") && !this.state.isFocusInside) {
        this.setState({
          isFocusInside: true
        });
      }
    };
    this.handleBlur = (event) => {
      var _a2;
      if (!((_a2 = this.element.current) == null ? void 0 : _a2.contains(event.relatedTarget)) && this.state.isFocusInside) {
        this.setState({
          isFocusInside: false
        });
      }
    };
    this.state = {
      hasScrolled: false,
      isBottomMainVisible: true,
      isLargeScreen: false,
      isMounted: false,
      isOverlayVisible: false,
      isScrollingTop: false,
      openSection: (_a = props.defaultOpenSection) != null ? _a : null,
      isSearchMinimized: false,
      isFocusInside: false
    };
  }
  componentDidMount() {
    this.prevScrollTop = window.scrollY || (document.documentElement ? document.documentElement.scrollTop : 0);
    window.addEventListener("scroll", this.handleScroll);
    window.addEventListener("resize", this.handleResize);
    this.setState({
      isLargeScreen: window.matchMedia("(min-width: ".concat(this.props.media.width.lg.min, ")")).matches,
      isMounted: true
    });
    this.context.toggleHeaderHidden(this.isBottomMainHidden());
    if (this.bottomMainElement.current) {
      this.context.setHeaderBottomHeight(this.bottomMainElement.current.clientHeight);
    }
    this.setHeaderHeight();
    this.handleMinimizedSearch();
  }
  componentWillUnmount() {
    window.removeEventListener("scroll", this.handleScroll);
    window.removeEventListener("resize", this.handleResize);
  }
  componentDidUpdate(prevProps, prevState) {
    const hasStateChanged = typeof this.props.openSection === "undefined" && prevState.openSection !== this.state.openSection;
    const hasPropsChanged = typeof this.props.openSection !== "undefined" && prevProps.openSection !== this.props.openSection;
    if (this.state.isLargeScreen && !prevState.isLargeScreen && this.openSection === "nav") {
      this.onOpenSectionChange(null, "nav");
    }
    if (hasStateChanged || hasPropsChanged) {
      if (this.openSection === "search") {
        return;
      }
    }
    this.context.toggleHeaderHidden(this.isBottomMainHidden());
    if (this.bottomMainElement.current) {
      this.context.setHeaderBottomHeight(this.bottomMainElement.current.clientHeight);
    }
    if (!this.state.hasScrolled) {
      this.setHeaderHeight();
    }
  }
  get openSection() {
    return this.isOpenSectionControlled(this.props.openSection) ? this.props.openSection : this.state.openSection;
  }
  isOpenSectionControlled(value = this.props.openSection) {
    return !!this.props.onOpenSectionChange && typeof value !== "undefined";
  }
  onOpenSectionChange(nextOpenSection, prevOpenSection) {
    if (!this.isOpenSectionControlled()) {
      this.setState({
        openSection: nextOpenSection
      });
    }
    if (this.props.onOpenSectionChange) {
      this.props.onOpenSectionChange(nextOpenSection, prevOpenSection);
    }
  }
  setHeaderHeight() {
    if (this.element.current) {
      this.context.setHeaderHeight(this.element.current.clientHeight);
    }
  }
  BEM() {
    const classArray = ["header"];
    if (this.isBottomMainHidden()) {
      classArray.push("has-scrolled");
    }
    if (this.openSection) {
      classArray.push("header--open-" + this.openSection);
    }
    if (this.state.isOverlayVisible) {
      classArray.push("header--has-overlay");
    }
    if (this.props.className) {
      classArray.push(this.props.className);
    }
    if (this.state.isSearchMinimized) {
      classArray.push("header--minimized-search");
    }
    return classArray.join(" ");
  }
  shouldShowOverlay() {
    const { isBottomMainVisible } = this.state;
    const { actions } = this.props;
    if (isBottomMainVisible && this.openSection && actions) {
      const currentAction = actions.items.find((a) => a.id === this.openSection);
      if (currentAction) {
        return typeof currentAction.showOverlay === "undefined" ? true : currentAction.showOverlay;
      }
    }
    return false;
  }
  getOverlay() {
    return /* @__PURE__ */ React.createElement(
      Transition,
      {
        in: this.shouldShowOverlay(),
        timeout: { enter: 10, exit: 100 },
        mountOnEnter: true,
        unmountOnExit: true,
        onExited: this.handleOverlayExited,
        onEntering: this.handleOverlayEntered,
        nodeRef: this.overlayRef
      },
      (state) => {
        return /* @__PURE__ */ React.createElement("div", { className: "header__overlay is-" + state, ref: this.overlayRef });
      }
    );
  }
  getNav(nav, secondaryItemsBefore = false) {
    return /* @__PURE__ */ React.createElement(
      HeaderNav,
      {
        ...nav,
        isOpen: this.openSection === "nav",
        onItemClick: this.handleItemClick,
        onItemToggle: this.handleItemToggle,
        dropdownContainer: this.dropdownContainer,
        secondaryItemsBefore
      }
    );
  }
  getActions(actions, location) {
    return /* @__PURE__ */ React.createElement(
      HeaderActions,
      {
        ...actions,
        onActionClick: this.onActionClick,
        currentSection: this.openSection,
        dropdownContainer: this.dropdownContainer,
        checkIfDropdownShouldBeVisible: this.shouldShowSection,
        dropdownOffsetY: 8,
        nav: {
          dropdownContent: this.renderNavDropdownContent(),
          dropdownPadding: "none",
          dropdownRef: (element) => this.navDropdownContent = element
        },
        location
      }
    );
  }
  renderAccessibilityLink(accessibility) {
    const { label, url, onClick } = accessibility;
    return /* @__PURE__ */ React.createElement(Anchor, { className: "header__accessibility text-weight-normal", url, onClick, isTextColor: true, isUnderlined: false }, label);
  }
  renderNavDropdownContent() {
    return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Container, { className: "header__dropdown-content-top" }, /* @__PURE__ */ React.createElement(Grid, { noWrap: true, align: ["middle-xs"], gutter: "none" }, /* @__PURE__ */ React.createElement(GridColumn, { width: ["min"] }, this.props.accessibility && this.renderAccessibilityLink(this.props.accessibility)), /* @__PURE__ */ React.createElement(GridColumn, { width: ["max"] }, this.props.languages && /* @__PURE__ */ React.createElement(HeaderLanguages, { ...this.props.languages })))), this.props.nav && this.getNav(this.props.nav, true));
  }
  getSearch(headerSearch) {
    const BEM = () => {
      const classArray = ["header__bottom-search-wrapper"];
      if (this.shouldShowSection("search")) {
        classArray.push("is-open");
      }
      return classArray.join(" ");
    };
    return /* @__PURE__ */ React.createElement("div", { className: BEM() }, /* @__PURE__ */ React.createElement(HeaderSearch, { ...headerSearch }));
  }
  topRightBEM() {
    var _a, _b;
    const classArray = ["header__top-right"];
    if ((_b = (_a = this.props.actions) == null ? void 0 : _a.sso) == null ? void 0 : _b.isLoggedIn) {
      classArray.push("logged-in");
    }
    return classArray.join(" ");
  }
  // Returns all actions' locations as a responsive object
  refactoredActions() {
    if (this.props.actions) {
      return {
        ...this.props.actions,
        items: this.props.actions.items.map((item) => {
          if (item.id === "nav") {
            return {
              ...item,
              location: { xs: "bottom", lg: null }
            };
          } else if (typeof item.location === "string") {
            return { ...item, location: { xs: item.location } };
          } else if (!item.location) {
            return { ...item, location: { xs: "bottom" } };
          } else {
            return { ...item, location: item.location };
          }
        })
      };
    }
  }
  // Returns actions' location based on its responsive location object and current breakpoint
  getLocation(currentBreak, locations) {
    const remToNumber = (value) => {
      return parseFloat(value.split("rem")[0]);
    };
    let currentLocation = null;
    if (currentBreak && locations) {
      for (let i = 0; i < breakPointOrder.length; i++) {
        const breakpoint = breakPointOrder[i];
        if (remToNumber(this.props.media.width[breakpoint].min) <= remToNumber(this.props.media.width[currentBreak].min)) {
          if (locations[breakpoint] !== void 0) {
            if (locations[breakpoint] === null) {
              return null;
            }
            currentLocation = locations[breakpoint];
          }
        } else {
          break;
        }
      }
      return currentLocation;
    }
  }
  getTop() {
    const refactoredActions = this.refactoredActions();
    const getTopActions = () => {
      if (refactoredActions) {
        return {
          ...refactoredActions,
          items: refactoredActions.items.filter(({ location }) => this.getLocation(this.props.currentBreak, location) === "top")
        };
      }
    };
    const topActions = getTopActions();
    return /* @__PURE__ */ React.createElement("div", { className: "header__top" }, /* @__PURE__ */ React.createElement(Container, null, /* @__PURE__ */ React.createElement("div", { className: "header__top-inner" }, /* @__PURE__ */ React.createElement("div", { className: "header__top-left" }, this.props.segment && /* @__PURE__ */ React.createElement(HeaderSegment, { scrollable: true, ...this.props.segment })), /* @__PURE__ */ React.createElement("div", { className: this.topRightBEM() }, this.props.accessibility && this.renderAccessibilityLink(this.props.accessibility), this.props.languages && /* @__PURE__ */ React.createElement(HeaderLanguages, { ...this.props.languages }), (topActions == null ? void 0 : topActions.items.length) ? this.getActions(topActions, "top") : void 0))));
  }
  getEmployeeHeader() {
    if (!this.props.employee) {
      return null;
    }
    return /* @__PURE__ */ React.createElement(HeaderEmployee, { ...this.props.employee, openSection: this.openSection, onActionClick: this.onActionClick, dropdownContainer: this.dropdownContainer });
  }
  get dropdownContainer() {
    var _a;
    return this.state.isMounted ? (_a = this.props.portalContainer) != null ? _a : document.body : null;
  }
  isBottomMainHidden() {
    return this.state.hasScrolled && !this.state.isScrollingTop && !this.openSection && !this.state.isFocusInside;
  }
  logoBEM() {
    const { logo } = this.props;
    const classArray = ["header__logo"];
    if (logo == null ? void 0 : logo.className) {
      classArray.push(logo.className);
    }
    return classArray.join(" ");
  }
  shouldShowTop() {
    const { accessibility, segment, languages } = this.props;
    const refactoredActions = this.refactoredActions();
    if (!segment && !languages && !accessibility) {
      if (!refactoredActions) {
        return false;
      }
      if (refactoredActions && refactoredActions.items.length) {
        return refactoredActions.items.some((i) => this.getLocation(this.props.currentBreak, i.location) === "top");
      }
    }
    return true;
  }
  getBottomMain() {
    const notices = Array.isArray(this.props.overlayNotice) ? this.props.overlayNotice : this.props.overlayNotice ? [this.props.overlayNotice] : [];
    const refactoredActions = this.refactoredActions();
    const getBottomActions = () => {
      if (refactoredActions) {
        return {
          ...refactoredActions,
          items: refactoredActions.items.filter(({ location }) => this.getLocation(this.props.currentBreak, location) === "bottom")
        };
      }
    };
    const bottomActions = getBottomActions();
    const content = (state) => /* @__PURE__ */ React.createElement("div", { className: "header__bottom-main", "data-state": state, ref: this.bottomMainElement }, this.shouldShowTop() && this.getTop(), /* @__PURE__ */ React.createElement("div", { className: "header__bottom" }, /* @__PURE__ */ React.createElement(Container, { ref: this.containerRef }, /* @__PURE__ */ React.createElement("div", { className: "header__bottom-inner" }, /* @__PURE__ */ React.createElement("div", { className: "header__bottom-left" }, this.props.logo && /* @__PURE__ */ React.createElement(Logo, { ...this.props.logo, className: this.logoBEM(), size: "small" }), this.props.nav && this.getNav(this.props.nav)), /* @__PURE__ */ React.createElement("div", { className: "header__bottom-right" }, /* @__PURE__ */ React.createElement("div", { className: "header__bottom-right-search" }, this.props.search && this.getSearch(this.props.search)), (bottomActions == null ? void 0 : bottomActions.items.length) ? this.getActions(bottomActions, "bottom") : void 0))), /* @__PURE__ */ React.createElement("div", { className: "header__bottom-shadow" })), /* @__PURE__ */ React.createElement("div", { "aria-live": "polite", role: "status" }, /* @__PURE__ */ React.createElement(TransitionGroup, { className: "header__overlay-notices-wrapper" }, notices.map((notice, index) => {
      return /* @__PURE__ */ React.createElement(
        HeaderOverlayNotice,
        {
          key: notice.id ? "".concat(notice.id, "-overlay-notice") : index,
          timeout: { enter: 500, exit: 500 },
          classNames: {
            enter: "is-entering",
            enterActive: "is-entered",
            exit: "is-exiting",
            exitActive: "is-exited"
          },
          notice
        }
      );
    }))));
    return /* @__PURE__ */ React.createElement(
      Transition,
      {
        in: this.isBottomMainHidden(),
        timeout: { enter: 10, exit: 500 },
        onExited: this.handleBottomMainExited,
        onEntered: this.handleBottomMainEntered,
        nodeRef: this.bottomMainElement
      },
      content
    );
  }
  get headerInnerContextValue() {
    return {
      openSection: this.openSection,
      setOpenSection: this.setOpenSection,
      isSearchMinimized: this.state.isSearchMinimized,
      containerRef: this.containerRef
    };
  }
  render() {
    return /* @__PURE__ */ React.createElement(HeaderInnerContext.Provider, { value: this.headerInnerContextValue }, /* @__PURE__ */ React.createElement("header", { className: this.BEM(), ref: this.element, id: "header", onFocus: this.handleFocus, onBlur: this.handleBlur }, this.props.skipLinks && this.props.skipLinks.length > 0 && /* @__PURE__ */ React.createElement(SkipLinks, { links: this.props.skipLinks, setHeaderHeight: () => this.setHeaderHeight() }), /* @__PURE__ */ React.createElement("div", { className: "header__main" }, this.props.employee && this.getEmployeeHeader(), this.getBottomMain()), this.getOverlay(), this.state.isMounted && /* @__PURE__ */ React.createElement(MediaQuery, { minWidth: this.props.media.width.lg.min, onChange: this.onMediaQueryChange }, null)));
  }
};
_HeaderInner.contextType = HeaderContext;
let HeaderInner = _HeaderInner;
export {
  Header
};
